import {Stack, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';

import {APIError} from 'data/errors/api-error';
import {HttpStatusCode} from 'data/protocols/http';
import {TypeLocalNameEnum} from 'domain/usecases';
import {GetExamsDistrictsParams} from 'domain/usecases/private/booking-flow/exams/get-exams-districts';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {AlertModal, CTAButtonFixed} from 'presentation/components';
import {
	BookingEmptyState,
	BookingErrorState,
	BookingLoadingState,
	BookingNotFoundListCard,
	BookingNotFoundState,
} from 'presentation/pages/private/booking/components';
import {BookingNotFoundVariantEnum} from 'presentation/pages/private/booking/components/BookingNotFoundDialog/types';
import {BookingNotFoundStateVariantEnum} from 'presentation/pages/private/booking/components/BookingNotFoundState/types';
import {FlowEnum, PATH_TITLE} from 'presentation/pages/private/booking/constants';
import {useGetExamsDistrictsQuery} from 'presentation/pages/private/booking/queries/useGetExamsDistrictsQuery';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {getPageHeaderTitle} from 'presentation/utils';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {DistrictsProps} from '../../types';
import {parseCommaSeparated} from '../../utils/parseCommaSeparated';
import {DistrictList} from '../DistrictList';

const {
	loadingError,
	loadingState,
	emptySearchState,
	notFoundModal: {cardListText},
	emptyModal: {part1, part2},
} = translations['pt-br'].privateFlow.bookingFlow.districtFlow;
const {booking} = translations['pt-br'].others.sidebar;
const {all, goForward, selectAll, notice, understood} = translations['pt-br'].commons;

export function ExamsDistricts({handlePrevStep, handleNextStep}: DistrictsProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {SEARCH_PARAMS} = useQueryParams();
	const {query} = SEARCH_PARAMS;
	const bookingDistricts = parseCommaSeparated(bookingData?.districtName);
	const bookingCities = parseCommaSeparated(bookingData?.citiesNames);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const districtsInitialState = bookingDistricts.map((d, i) => `${d}-${bookingCities[i] ?? ''}`);
	const [selectedDistricts, setSelectedDistricts] = useState<string[]>(districtsInitialState);
	const [showEmptyDistrictsModal, setShowEmptyDistrictsModal] = useState(false);
	const {isMobile} = useMediaQueries();
	const pageTitle = getPageHeaderTitle({
		internalPaths: PATH_TITLE,
		mainPath: booking,
	});

	const districtParams: GetExamsDistrictsParams = {
		cdUf: bookingData.ufCode?.toUpperCase(),
		nmLocal: bookingData.cityName?.toUpperCase(),
		nmTipoLocal: bookingData.typeLocalName || TypeLocalNameEnum.CIDADE,
		cdTipoExame: bookingData.specialtyCode,
	};

	const {
		refetch,
		isError: districtListHasError,
		isFetching: districtListIsLoading,
		data: districtList,
		error: districtListError,
	} = useGetExamsDistrictsQuery(districtParams);

	const districtListHasUnprocessableError =
		districtListHasError &&
		districtListError instanceof APIError &&
		districtListError.statusCode === HttpStatusCode.unprocessable;

	const NOT_FOUND =
		!districtListIsLoading &&
		(!districtList ||
			districtList.length === 0 ||
			(districtListHasError && districtListHasUnprocessableError));

	useEffect(() => {
		setHideSearchBarState(NOT_FOUND);
	}, [setHideSearchBarState, NOT_FOUND]);

	if (districtListHasError && !districtListHasUnprocessableError) {
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={pageTitle}
				errorTitle={loadingError}
				handleBack={handlePrevStep}
				handleRefresh={refetch}
			/>
		);
	}

	if (districtListIsLoading) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={pageTitle}
				loadingText={loadingState}
				handleBack={handlePrevStep}
			/>
		);
	}

	if (NOT_FOUND) {
		return (
			<BookingNotFoundState
				showHeader={false}
				flowType={FlowEnum.EXAM}
				pageTitle={pageTitle}
				handleBack={handlePrevStep}
				variant={BookingNotFoundStateVariantEnum.DISTRICT}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_BAIRRO_DISPONIVEL}
			/>
		);
	}

	const handleSelectCard = (district: string, skipScroll?: boolean) => {
		if (district === `${all}-${selectAll}`) {
			const newAllSelected = !bookingData.allDistrictsSelected;
			const districts = districtList?.map(d => d.district);
			const cities = districtList?.map(d => d.city);

			setBookingData(prev => ({
				...prev,
				districtName: newAllSelected ? districts?.join(', ') : '',
				citiesNames: newAllSelected ? cities?.join(', ') : '',
				allDistrictsSelected: newAllSelected,
			}));

			setSelectedDistricts(
				newAllSelected ? (districtList?.map(d => `${d.district}-${d.city}`) ?? []) : [],
			);

			const shouldScrollToForwardButton =
				!isMobile && newAllSelected && districts && districts.length >= 12 && !skipScroll;

			if (shouldScrollToForwardButton) {
				document.getElementById('go-forward-button')?.scrollIntoView({behavior: 'smooth'});
			}
			return;
		}

		setSelectedDistricts(prevSelected => {
			let updatedSelected: string[];

			if (prevSelected.includes(district)) {
				updatedSelected = prevSelected.filter(item => item !== district);
			} else {
				updatedSelected = [...prevSelected, district];
			}

			const districts = updatedSelected?.map(item => item?.split('-')[0]);
			const cities = updatedSelected?.map(item => item?.split('-')[1]);

			setBookingData(prev => ({
				...prev,
				districtName: districts.join(', '),
				citiesNames: cities.join(', '),
				allDistrictsSelected: updatedSelected.length === districtList?.length,
			}));

			return updatedSelected;
		});
	};

	const handleGoForward = () => {
		if (selectedDistricts.length === 0) {
			setShowEmptyDistrictsModal(true);
			return;
		}
		handleNextStep();
	};

	const FILTERED_DISTRICT_LIST =
		districtList?.filter(districtItem =>
			districtItem.district.toLowerCase().includes(query.trim().toLowerCase()),
		) ?? [];

	const showEmptyState = FILTERED_DISTRICT_LIST?.length === 0;
	if (showEmptyState) {
		return (
			<BookingEmptyState
				description={emptySearchState.emptyDistrictAll}
				flowType={FlowEnum.EXAM}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_BAIRRO_ENCONTRADO}
			/>
		);
	}

	return (
		<Stack gap={2}>
			<DistrictList
				list={FILTERED_DISTRICT_LIST}
				handleSelectCard={handleSelectCard}
				selectedDistricts={selectedDistricts}
			/>
			<Stack mt={2} id="go-forward-button">
				<BookingNotFoundListCard
					flowType={FlowEnum.EXAM}
					customOrientationText={cardListText}
					variant={BookingNotFoundVariantEnum.DISTRICT}>
					<CTAButtonFixed.Root>
						<CTAButtonFixed.Action fullWidth onClick={handleGoForward} size="large">
							{goForward}
						</CTAButtonFixed.Action>
					</CTAButtonFixed.Root>
				</BookingNotFoundListCard>
			</Stack>

			{showEmptyDistrictsModal ? (
				<AlertModal
					open={showEmptyDistrictsModal}
					headerTitle={notice}
					variant="info"
					onClose={() => setShowEmptyDistrictsModal(false)}
					buttonLabel={understood}
					slotProps={{
						paper: {
							style: {
								maxWidth: 420,
								width: '100%',
							},
						},
					}}
					subDescription={
						<Typography variant="h6" fontSize={18} textAlign="center" fontWeight={600}>
							{part1} <br /> {part2}
						</Typography>
					}
				/>
			) : null}
		</Stack>
	);
}
